import { useState } from 'react';
import clsx from 'clsx';
import {
  TrendingUp,
  Activity,
  Wind,
  Brain,
  Shield,
  XCircle,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Cpu,
} from 'lucide-react';
import type { BetRecommendation, AgentName, AgentOpinion } from '../../types';

const AGENT_META: Record<AgentName, { label: string; icon: React.ElementType; color: string }> = {
  market: { label: 'Market', icon: BarChart3, color: 'text-brand' },
  matchup: { label: 'Matchup', icon: Activity, color: 'text-purple-400' },
  form: { label: 'Form', icon: TrendingUp, color: 'text-win' },
  context: { label: 'Context', icon: Wind, color: 'text-info' },
  risk: { label: 'Risk', icon: Shield, color: 'text-warn' },
  validation: { label: 'Validation', icon: XCircle, color: 'text-loss' },
  portfolio: { label: 'Portfolio', icon: Brain, color: 'text-purple-400' },
  closingLineValue: { label: 'Closing Line Value', icon: TrendingUp, color: 'text-brand' },
  mlModel: { label: 'ML Model', icon: Cpu, color: 'text-info' },
};

const AGENT_ORDER: AgentName[] = [
  'matchup',
  'form',
  'context',
  'market',
  'risk',
  'validation',
  'portfolio',
  'closingLineValue',
  'mlModel',
];

interface AgentPanelProps {
  recommendation: BetRecommendation;
}

export function AgentPanel({ recommendation }: AgentPanelProps) {
  const [expandedAgent, setExpandedAgent] = useState<AgentName | null>(null);

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center justify-between">
        <span>Agent Analysis</span>
        <span className="text-gray-600">
          Consensus: {(recommendation.consensusScore * 100).toFixed(0)}%
        </span>
      </div>

      {AGENT_ORDER.map((agentName) => {
        const opinion = recommendation.agentOpinions[agentName];
        if (!opinion) return null;
        const meta = AGENT_META[agentName];
        const isExpanded = expandedAgent === agentName;

        return (
          <AgentRow
            key={agentName}
            opinion={opinion}
            meta={meta}
            isExpanded={isExpanded}
            onToggle={() => setExpandedAgent(isExpanded ? null : agentName)}
          />
        );
      })}
    </div>
  );
}

interface AgentRowProps {
  opinion: AgentOpinion;
  meta: { label: string; icon: React.ElementType; color: string };
  isExpanded: boolean;
  onToggle: () => void;
}

function AgentRow({ opinion, meta, isExpanded, onToggle }: AgentRowProps) {
  const Icon = meta.icon;
  const scorePct = Math.round(opinion.score * 100);
  const isPositive = opinion.score > 0.6;
  const isNegative = opinion.score < 0.4;
  const isChallenge = opinion.signal === 'challenge';

  const barColor = isChallenge
    ? 'bg-loss'
    : isPositive
      ? 'bg-win'
      : isNegative
        ? 'bg-loss'
        : 'bg-gray-500';

  const scoreColor = isChallenge
    ? 'text-loss'
    : isPositive
      ? 'text-win'
      : isNegative
        ? 'text-loss/70'
        : 'text-gray-400';

  return (
    <div
      className={clsx(
        'rounded-md border overflow-hidden transition-colors',
        isExpanded ? 'border-surface-4 bg-surface-2' : 'border-transparent hover:border-surface-4',
        isChallenge && 'border-loss/20 bg-loss/5',
      )}
    >
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
        <Icon className={clsx('w-4 h-4 flex-shrink-0', meta.color)} />
        <span className="text-sm text-gray-300 flex-1">{meta.label}</span>

        {/* Score bar */}
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 bg-surface-4 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all', barColor)}
              style={{ width: `${scorePct}%` }}
            />
          </div>
          <span className={clsx('text-xs font-mono w-8 text-right', scoreColor)}>{scorePct}%</span>
        </div>

        {/* Confidence badge */}
        <span
          className={clsx(
            'text-xs px-1.5 py-0.5 rounded border',
            opinion.confidence === 'high'
              ? 'confidence-high'
              : opinion.confidence === 'medium'
                ? 'confidence-medium'
                : 'confidence-low',
          )}
        >
          {opinion.confidence[0].toUpperCase()}
        </span>

        {opinion.dataWarnings.length > 0 && (
          <span className="w-4 h-4 rounded-full bg-warn/20 text-warn text-[9px] flex items-center justify-center font-bold">
            !
          </span>
        )}

        {isExpanded ? (
          <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-gray-500 flex-shrink-0" />
        )}
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-surface-4">
          {/* Signal */}
          <div className="flex items-center gap-2 pt-2">
            <span className="text-xs text-gray-500">Signal:</span>
            <span
              className={clsx(
                'text-xs font-medium',
                isChallenge ? 'text-loss' : isPositive ? 'text-win' : 'text-gray-400',
              )}
            >
              {opinion.signal.replace(/_/g, ' ').toUpperCase()}
            </span>
          </div>

          {/* Reasoning */}
          {opinion.reasoning.length > 0 && (
            <div className="space-y-1.5">
              {opinion.reasoning.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <div
                    className={clsx(
                      'w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1',
                      r.direction === 'positive'
                        ? 'bg-win'
                        : r.direction === 'negative'
                          ? 'bg-loss'
                          : 'bg-gray-500',
                    )}
                  />
                  <span className="text-gray-400 leading-relaxed">{r.text}</span>
                  {r.dataPoint && (
                    <span className="font-mono text-gray-600 flex-shrink-0">[{r.dataPoint}]</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {opinion.dataWarnings.length > 0 && (
            <div className="pt-1 border-t border-surface-4 space-y-1">
              {opinion.dataWarnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-warn">
                  <span className="flex-shrink-0">⚠</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* Sources */}
          <div className="text-xs text-gray-600 pt-1 border-t border-surface-4">
            Sources: {opinion.sourcesUsed.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
}
